import {
  Box,
  Flex,
  Input,
  Option,
  RadioButton,
  Select,
  Text,
  Textarea,
  ValidationMessage,
} from '@modusign/design-system';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form-v7';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { validateEmail } from '@app/libs/validation';

import DocumentStatus from '../../types/DocumentStatus';
import { ExternalDocument } from '../../types/ExternalDocument';
import statusToString from '../../utils/statusToString';
import DetailInfoItem from '../DetailInfoItem';
import InternationalTelephoneInput from '../InternationalTelephoneInput';
import DateOfCompletionInput from './DateOfCompletion';
import DocumentPeriodInput from './DocumentPeriodInput';
import RenewalInput from './RenewalInput';

function validateContact(value: { email: string; phone: string } | null): string | true {
  if (!value) {
    return true;
  }

  const { email, phone } = value;

  let emailError = '';
  let phoneError = '';

  if (!email && !phone) {
    emailError = '담당자 이메일을 입력해 주세요.';
    phoneError = '담당자 번호를 입력해 주세요.';
  }

  const emailValidation = validateEmail(email);
  if (email && !emailValidation.success) {
    emailError = emailValidation.message;
  }

  if (phone && !isValidPhoneNumber(phone)) {
    phoneError = '정확한 번호를 입력해 주세요.';
  }

  if (emailError || phoneError) {
    return [emailError, phoneError].join('|');
  }

  return true;
}

export interface BasicInformationFormState {
  title: string;
  category: { id: string; name: string } | null;
  type: 'PAPER' | 'ELECTRONIC' | null;
  status: DocumentStatus | '';
  personInCharge: {
    name: string;
    contact: { email?: string; phone?: string } | null;
  };
  originalLocation: string;
  memo: string;
  dateOfCompletion: string | null;
  documentPeriod: {
    from: string | null;
    to: string | null;
  };
  renewalRadio: '0' | '1';
  renewal?: {
    noticeBeforeExpirationInDays: -1 | 30 | 60 | 90 | 120;
    periodInYears: number;
  };
}

interface BasicInformationEditorProps {
  // TODO: (@abel): 업로드 페이지에서 제목에 focus를 주는 더 나은 방법 생각해서 리팩토링 하기
  // 업로드 페이지에서 임의로 제목을 focus하기 위해 사용합니다.
  focusTitleInput?: boolean;
  defaultExternalDocument?: ExternalDocument;
}

export default function BasicInformationEditor({
  focusTitleInput,
  defaultExternalDocument,
}: BasicInformationEditorProps) {
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const {
    register,
    formState: { errors },
  } = useFormContext<BasicInformationFormState>();

  useEffect(() => {
    if (focusTitleInput && titleInputRef.current) {
      // 업로드 페이지에서 파일 선택 후 에디터가 나타날 때 setTimeout을 사용해야 해야 focus가 됩니다.
      // 비동기 문제로 예상하나 정확한 문제를 파악하진 못해서 일단 setTimeout으로 해결합니다.
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 0);
    }
  }, [focusTitleInput]);

  const typeFieldProps = register('type');

  return (
    <Flex
      borderTop="1px solid"
      borderTopColor="lightGray300"
      flexDirection="column"
      minHeight="60px"
      mt={7}
    >
      <DetailInfoItem name="계약 제목" required>
        <Box width="100%">
          <Input
            defaultValue={defaultExternalDocument?.title}
            isInvalid={!!errors.title}
            maxLength={101}
            placeholder="1자 이상 100자 이내"
            {...register('title', {
              required: '계약 제목을 입력해 주세요.',
              maxLength: {
                value: 100,
                message: '최대 입력 가능한 길이는 100자입니다.',
              },
            })}
            required
          />
          {errors.title && <ValidationMessage>{errors.title.message}</ValidationMessage>}
        </Box>
      </DetailInfoItem>
      <DetailInfoItem name="계약 체결일" wrapperFlex="1 1 0">
        <DateOfCompletionInput />
      </DetailInfoItem>
      <DetailInfoItem name="계약서 형태" wrapperFlex="1 1 0">
        <Flex flexWrap="wrap" sx={{ gap: '12px' }}>
          <Flex
            alignItems="center"
            as="label"
            display="flex"
            fontWeight="medium"
            gap={3}
            whiteSpace="nowrap"
          >
            <RadioButton
              defaultChecked={defaultExternalDocument?.type === 'PAPER'}
              {...typeFieldProps}
              value="PAPER"
              onClick={(event) => typeFieldProps.onChange(event)}
            />
            <span>종이 계약</span>
          </Flex>
          <Flex
            alignItems="center"
            as="label"
            display="flex"
            fontWeight="medium"
            gap={3}
            whiteSpace="nowrap"
          >
            <RadioButton
              defaultChecked={defaultExternalDocument?.type === 'ELECTRONIC'}
              {...typeFieldProps}
              value="ELECTRONIC"
              onClick={(event) => typeFieldProps.onChange(event)}
            />
            <span>기타 전자계약</span>
          </Flex>
        </Flex>
      </DetailInfoItem>
      <DetailInfoItem name="계약 기간" wrapperFlex="1 1 0">
        <DocumentPeriodInput />
      </DetailInfoItem>
      <DetailInfoItem name="계약 상태" wrapperFlex="1 1 0">
        <Select defaultValue={defaultExternalDocument?.status || ''} {...register('status')}>
          <Option value="">계약 상태를 선택해 주세요.</Option>
          {(['EFFECTIVE', 'EXPIRED', 'ABORTED'] as const).map((status) => (
            <Option key={status} value={status}>
              {statusToString(status)}
            </Option>
          ))}
        </Select>
      </DetailInfoItem>
      <DetailInfoItem name="계약 갱신 여부">
        <RenewalInput />
      </DetailInfoItem>
      <DetailInfoItem name="담당자 이름" required>
        <Box width="100%">
          <Input
            defaultValue={defaultExternalDocument?.personInCharge?.name || undefined}
            isInvalid={!!errors.personInCharge?.name}
            maxLength={31}
            placeholder="이름"
            {...register('personInCharge.name', {
              required: '담당자 이름을 입력해 주세요.',
              maxLength: {
                value: 30,
                message: '최대 입력 가능한 길이는 30자입니다.',
              },
            })}
            required
          />
          {errors.personInCharge?.name && (
            <ValidationMessage>{errors.personInCharge?.name?.message}</ValidationMessage>
          )}
        </Box>
      </DetailInfoItem>
      <DetailInfoItem name="담당자 연락처" required>
        <Box width="100%">
          <Controller
            defaultValue={{ email: '', phone: '' }}
            name="personInCharge.contact"
            render={({ field: { onChange, value } }) => {
              const [emailError, phoneError] =
                errors.personInCharge?.contact?.message?.split('|') || [];

              return (
                <Flex flexDirection={['column', null, 'row']}>
                  <Box flex="1 1 0" mb={[3, null, 0]} mr={[null, null, 3]}>
                    <Input
                      isInvalid={!!emailError}
                      placeholder="이메일 주소"
                      value={value?.email}
                      onChange={(e) => onChange({ ...value, email: e.target.value })}
                    />
                    {emailError && <ValidationMessage>{emailError}</ValidationMessage>}
                  </Box>
                  <Box flex="1 1 0">
                    <InternationalTelephoneInput
                      isInvalid={!!phoneError}
                      value={value?.phone}
                      onChange={(v) => {
                        onChange({ ...value, phone: v });
                      }}
                    />
                    {phoneError && <ValidationMessage>{phoneError}</ValidationMessage>}
                  </Box>
                </Flex>
              );
            }}
            rules={{ required: true, validate: validateContact }}
          />
          <Text as="p" color="textNeutralLight" ml="10px" sx={{ lineHeight: 1.5, mt: 3 }}>
            최소 한 개의 연락처를 입력해 주세요.
          </Text>
        </Box>
      </DetailInfoItem>
      <DetailInfoItem name="원본 보관 위치">
        <Box width="100%">
          <Input
            defaultValue={defaultExternalDocument?.originalLocation || undefined}
            isInvalid={!!errors.originalLocation}
            maxLength={257}
            placeholder="원본 보관 위치"
            {...register('originalLocation', {
              maxLength: {
                value: 256,
                message: '최대 입력 가능한 길이는 256자입니다.',
              },
            })}
          />
          {errors.originalLocation && (
            <ValidationMessage>{errors.originalLocation?.message}</ValidationMessage>
          )}
        </Box>
      </DetailInfoItem>
      <DetailInfoItem name="메모">
        <Box width="100%">
          <Textarea
            defaultValue={defaultExternalDocument?.memo || undefined}
            height="98px"
            isInvalid={!!errors.memo}
            maxLength={201}
            placeholder="메모"
            {...register('memo', {
              maxLength: {
                value: 200,
                message: '최대 입력 가능한 길이는 200자입니다.',
              },
            })}
          />
          {errors.memo && <ValidationMessage>{errors.memo?.message}</ValidationMessage>}
        </Box>
      </DetailInfoItem>
    </Flex>
  );
}
